import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Transition } from '@headlessui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { sidebarMenus } from './menus'
import { useGAEvent } from '../ReactGA';
import { customEvents } from '../../../utils/customEvents';
import { Chip } from '..'

export const Sidebar = React.memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const sendGAEvent = useGAEvent();
    const { user } = useSelector(state => state.auth);
    const { role, customerData: { region = '' } = {} } = user;
    const [menu, setMenu] = useState([])

    const isMenuActive = React.useCallback((sidebarMenu) => {
        const { path = "" } = sidebarMenu;
        //eslint-disable-next-line 
        const [_ = "", secondPath = ""] = location.pathname?.split('/') || []
        const selected = Boolean(secondPath === path?.replace('/', ''))
        return selected;
    }, [location?.pathname])

    const getPath = (path) => {
        const paths = {
            '/teams': customEvents.GA_TEAMS,
            '/clients': customEvents.GA_CLIENTS,
            '/dashboard': customEvents.GA_DASHBOARD,
            '/roles': customEvents.GA_ROLE_SYNC,
            '/help-desk': customEvents.GA_HELPDESK,
            '/news-alerts': customEvents.GA_NEWS_AND_ALERTS,
            '/manage-sla': customEvents.GA_MANAGE_SLA,

        }
        return paths[path] || {}
    }

    const handleClick = ({ path, children, title }) => {
        if (!children?.length) {
            sendGAEvent(getPath(path))
            navigate(path);
        } else {
            const newMenu = [...menu];
            const index = newMenu?.findIndex(menu => menu?.title === title);
            newMenu[index].expanded = !newMenu[index].expanded;
            setMenu(newMenu)
        }
    }

    const isSubMenuActive = React.useCallback((array) => {
        const selected = array?.length && array?.find((menu) => menu?.path?.substring(0, 3) === location?.pathname?.substring(0, 3))
        return Boolean(location?.pathname?.substring(0, 3) === selected?.path?.substring(0, 3))
    }, [location?.pathname])

    React.useEffect(() => {
        if (sidebarMenus?.length) {
            const newSidebarMenus = [...sidebarMenus]?.map(menu => ({
                ...menu,
                expanded: isSubMenuActive(menu?.children),
                children: (menu?.children?.length && menu?.children?.filter(child => {
                    const hasRolePermission = child?.permissions?.includes(role) || child?.permissions?.includes("*");
                    const hasRegionPermission = !child?.region?.length || child?.region?.includes(region);
                    return hasRolePermission && hasRegionPermission;
                })) || [],
            }))?.filter(menu => {
                const hasRolePermission = menu?.permissions?.includes(role) || menu?.permissions?.includes("*");
                const hasRegionPermission = !menu?.region?.length || menu?.region?.includes(region);
                return hasRolePermission && hasRegionPermission;
            })
            setMenu(newSidebarMenus);
        }
    }, [isMenuActive, isSubMenuActive, role, region])

    return (
        <div className="sidebar-nav">
            <ul className="max-h-[70vh] overflow-x-hidden overflow-y-auto">
                {!!menu?.length && menu?.map(sidebarMenu =>
                    <li key={sidebarMenu?.path} className="mb-2.5">
                        <span onClick={() => handleClick(sidebarMenu)} className={`flex cursor-pointer ${isMenuActive(sidebarMenu) ? 'active bg-primary-50 items-center pr-4 rounded-6 w-full' : ''}`}>
                            <span className={`w-1.5 h-8 mr-2.5 ${isMenuActive(sidebarMenu) ? 'bg-primary-500' : ''} rounded-r-14`}></span>
                            <span className="flex items-center mr-2.5">
                                {isMenuActive(sidebarMenu) ? sidebarMenu?.activeIcon : sidebarMenu?.icon}
                            </span>
                            <div className='flex items-center gap-2'>
                                <span className="text-secondary-400 text-sm flex align-middle items-center">{sidebarMenu?.title}</span>
                                {sidebarMenu?.badge && <Chip variant="chip-success" className="h-fit w-fit text-[10px]" text={sidebarMenu?.badge} />}
                            </div>
                        </span>
                        <Transition show={!!sidebarMenu?.children?.length && sidebarMenu?.expanded}
                            enter="transition-opacity duration-200"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <ul className='mx-10 my-2 gap-1 flex flex-col border-l-2 border-solid border-secondary-100 '>
                                {sidebarMenu?.children?.map(subMenu => <React.Fragment key={subMenu?.title}>
                                    <li onClick={() => handleClick(subMenu)} className={`flex w-full py-0.5 ${isMenuActive(subMenu) ? 'bg-primary-50' : ''} cursor-pointer rounded-r-4`}>
                                        <span className={`w-1.5 h-8 mr-2.5 ${isMenuActive(subMenu) ? 'bg-primary-500' : ''} rounded-r-14`}></span>
                                        <span className='mx-2 text-secondary-400 text-sm flex align-middle items-center'>
                                            {subMenu?.title}
                                        </span>
                                    </li>
                                </React.Fragment>
                                )}
                            </ul>
                        </Transition>
                    </li>)}
            </ul>
        </div>
    )
})
